import type { ReactNode } from 'react';

export interface PersonTabDef {
  readonly id: string;
  readonly label: string;
  readonly disabled?: boolean;
}

/**
 * A small native tab bar — no library, no existing precedent in the app to
 * copy (this is the first tabbed view in the codebase). Explicit ARIA
 * (`role="tab"`/`aria-selected`/`aria-controls`), matching the manual-ARIA
 * discipline `Field`/`Switch` already apply elsewhere. Active tab is local
 * component state in the caller, not the URL — the reference this mirrors
 * doesn't deep-link to a tab either.
 */
export function PersonTabs({
  tabs,
  active,
  onSelect,
  label,
}: {
  readonly tabs: readonly PersonTabDef[];
  readonly active: string;
  readonly onSelect: (id: string) => void;
  readonly label: string;
}) {
  return (
    <div role="tablist" className="person-tabs" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          id={`person-tab-${tab.id}`}
          aria-selected={active === tab.id}
          aria-controls={`person-panel-${tab.id}`}
          tabIndex={active === tab.id ? 0 : -1}
          disabled={tab.disabled}
          className="person-tab"
          onClick={() => onSelect(tab.id)}
        >
          {tab.label}
        </button>
      ))}
    </div>
  );
}

export function PersonTabPanel({
  id,
  active,
  children,
}: {
  readonly id: string;
  readonly active: string;
  readonly children: ReactNode;
}) {
  if (id !== active) return null;

  return (
    <div
      role="tabpanel"
      id={`person-panel-${id}`}
      aria-labelledby={`person-tab-${id}`}
      className="person-tab-panel"
    >
      {children}
    </div>
  );
}
